import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Button,
  IconButton,
  Chip,
} from "@mui/material";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import LockIcon from "@mui/icons-material/Lock";
import { useState, useRef } from "react";
import { observer } from "mobx-react-lite";
import { TextField, InputAdornment, CircularProgress } from "@mui/material";
import { useStore } from "../../stores/store";
import CloseIcon from "@mui/icons-material/Close";
import { ApplicableCoupon } from "../../models/Coupons";

interface AppliedCoupon {
  couponCode: string;
  couponId: string;
  message: string;
  isHidden?: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  coupons: ApplicableCoupon[];
  onApply: (couponCode: string) => void;
  appliedCoupon: AppliedCoupon | null;
  onCouponUpdate: (coupon: AppliedCoupon | null) => void;
  loadingCoupons?: boolean;
  deliveryType: number;
  readOnly?: boolean;
}

const CouponPopup: React.FC<Props> = observer(
  ({ open, onClose, coupons, onApply, appliedCoupon, onCouponUpdate, loadingCoupons, deliveryType, readOnly = false }) => {
    // Stores import
    const { cartStore, commonStore, toasterStore } = useStore();
    const { customer } = commonStore;
    const { verifyCoupon, getLoadingCouponVerify, getCartItems } =
      cartStore;

    // States
    const [isProcessing, setIsProcessing] = useState(false);
    const [manualCode, setManualCode] = useState("");

    const warehouseId = commonStore.warehouseId;
    const customerTypeId = customer?.customerTypeId;
    const outletId = customer?.id;
    const appliedCouponId = appliedCoupon?.couponId || null;
    const processingRef = useRef(false);

    const handleApplyCoupon = async (coupon: ApplicableCoupon) => {
      if (!coupon.isApplicable) return;
      if (isProcessing || processingRef.current) return;
      if (!warehouseId || !customerTypeId || !outletId) return;

      try {
        setIsProcessing(true);
        processingRef.current = true;

        const params = {
          couponId: coupon.id,
          deliveryType: deliveryType,
          warehouseId: warehouseId,
        };

        const headers = {
          customerTypeId: customerTypeId,
          outletId: outletId,
        };

        // Verify coupon API call
        const result = await verifyCoupon(params, headers);

        if (result?.status_code === 1 && result?.data?.isApplicable) {
          // Refresh cart with couponId to get updated totals
          await getCartItems({
            outletId: outletId,
            businessTypeId: customerTypeId,
            warehouseId: warehouseId,
            order_type: deliveryType,
            couponId: Number(coupon.id),
          });

          // Update applied coupon state from API response
          const appliedCouponData: AppliedCoupon = {
            couponCode: coupon.code,
            couponId: coupon.id,
            message: result?.message || "Coupon applied successfully",
            isHidden: coupon.isHidden ?? false,
          };

          onCouponUpdate(appliedCouponData);
          onApply(coupon.code);

          toasterStore.success(result?.message || "Coupon applied successfully!");
        } else {
          toasterStore.error(result?.message || "Coupon is not applicable");
        }
      } catch (error) {
        toasterStore.error("Failed to apply coupon");
      } finally {
        setIsProcessing(false);
        processingRef.current = false;
      }
    };

    const handleManualApply = async () => {
      const code = manualCode.trim();
      if (!code) return;
      if (isProcessing || processingRef.current) return;
      if (!warehouseId || !customerTypeId || !outletId) return;

      try {
        setIsProcessing(true);
        processingRef.current = true;

        const params = {
          couponCode: code,
          deliveryType: deliveryType,
          warehouseId: warehouseId,
        };

        const headers = {
          customerTypeId: customerTypeId,
          outletId: outletId,
        };

        const result = await verifyCoupon(params, headers);

        if (result?.status_code === 1 && result?.data?.isApplicable) {
          const couponData = result.data.coupon;

          await getCartItems({
            outletId: outletId,
            businessTypeId: customerTypeId,
            warehouseId: warehouseId,
            order_type: deliveryType,
            couponId: Number(couponData?.id),
          });

          const appliedCouponData: AppliedCoupon = {
            couponCode: couponData?.code || code,
            couponId: couponData?.id || "",
            message: result?.message || "Coupon applied successfully",
            isHidden: couponData?.isHidden ?? true,
          };

          onCouponUpdate(appliedCouponData);
          onApply(couponData?.code || code);
          setManualCode("");

          toasterStore.success(result?.message || "Coupon applied successfully!");
        } else {
          toasterStore.error(result?.message || "Coupon is not applicable");
        }
      } catch (error) {
        toasterStore.error("Failed to apply coupon");
      } finally {
        setIsProcessing(false);
        processingRef.current = false;
      }
    };

    const handleRemoveCoupon = async () => {
      if (isProcessing || processingRef.current) return;
      if (!warehouseId || !customerTypeId || !outletId) return;

      try {
        setIsProcessing(true);
        processingRef.current = true;

        // Refresh cart without couponId to remove coupon
        await getCartItems({
          outletId: outletId,
          businessTypeId: customerTypeId,
          warehouseId: warehouseId,
          order_type: deliveryType,
        });

        // Update applied coupon state
        onCouponUpdate(null);
        toasterStore.success("Coupon removed successfully!");
      } catch (error) {
        toasterStore.error("Failed to remove coupon");
      } finally {
        setIsProcessing(false);
        processingRef.current = false;
      }
    };

    const loading = getLoadingCouponVerify() || isProcessing;

    // Separate unlocked (applicable) and locked (not applicable) coupons
    const unlockedCoupons = coupons.filter((c) => c.isApplicable);
    const lockedCoupons = coupons.filter((c) => !c.isApplicable);

    // Helper function to generate unlock message for locked coupons
    const getUnlockMessage = (coupon: ApplicableCoupon): string | null => {
      if (coupon.isApplicable || !coupon.eligibilityRules?.length) return null;
      
      const rule = coupon.eligibilityRules[0];
      if (!rule || rule.isApplicable) return null;
      
      if (
        rule.ruleType === "UNIQUE_SKU" &&
        (rule as any).countRequired !== undefined
      ) {
        const count = (rule as any).countRequired;
        return `Add ${count} unique ${count > 1 ? "products" : "product"} to unlock`;
      }
      
      if (rule.ruleValue !== undefined) {
        return `Add items worth ₹${rule.ruleValue} to unlock`;
      }
      
      return null;
    };

    const renderCouponCard = (coupon: ApplicableCoupon, isLocked: boolean) => {
      const isApplied = appliedCouponId === coupon.id;
      const isApplicable = !isLocked;
      const discountDisplay =
        coupon.discountType === "PERCENT"
          ? `${coupon.discountValue}%`
          : `₹${coupon.discountValue}`;

      return (
        <Box
          key={coupon.id}
          display="flex"
          alignItems="center"
          bgcolor={isLocked ? "#fafafa" : "#fff"}
          borderRadius={2}
          p={2}
          mb={2}
          boxShadow="0 1px 4px rgba(0,0,0,0.1)"
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            px: { xs: 1, md: 2 },
            opacity: isLocked ? 0.75 : 1,
          }}
        >
          {/* Desktop Layout */}
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              width: "100%",
            }}
          >
            {/* Discount Badge */}
            <Box
              bgcolor={
                isApplied ? "#4CAF50" : isLocked ? "#9e9e9e" : "#F44336"
              }
              color="#fff"
              px={2}
              py={4}
              borderRadius="10px 0 0 10px"
              fontWeight={700}
              fontSize="0.9rem"
              textAlign="center"
              height="100%"
              minWidth="60px"
              position="relative"
            >
              {discountDisplay}
              {isLocked && (
                <LockIcon
                  sx={{
                    fontSize: 14,
                    position: "absolute",
                    top: 4,
                    right: 4,
                  }}
                />
              )}
            </Box>

            {/* Coupon Details */}
            <Box flex={1} ml={2} minWidth={0}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontWeight={600} sx={{ wordBreak: "break-word" }}>{coupon.name}</Typography>
                <Chip
                  label={coupon.discountConditionTypeName}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.7rem", height: 20 }}
                />
              </Box>
              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                Coupon Code: <strong>{coupon.code}</strong>
              </Typography>
              {coupon.description && (
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                  {coupon.description}
                </Typography>
              )}
              {isLocked && getUnlockMessage(coupon) && (
                <Typography variant="body2" color="success.main" fontWeight={500} mt={0.5}>
                  {getUnlockMessage(coupon)}
                </Typography>
              )}
            </Box>

            {/* Apply/Remove Button */}
            <Button
              onClick={() => {
                if (!readOnly) {
                  if (isApplied) {
                    handleRemoveCoupon();
                  } else {
                    handleApplyCoupon(coupon);
                  }
                }
              }}
              sx={{
                ml: 2,
                fontWeight: 600,
                flexShrink: 0,
                ...(isLocked && {
                  backgroundColor: "#757575 !important",
                  color: "#fff !important",
                  opacity: "1 !important",
                }),
                ...(readOnly && isApplicable && {
                  backgroundColor: "#4CAF50 !important",
                  color: "#fff !important",
                  opacity: "1 !important",
                }),
              }}
              endIcon={
                isLocked ? (
                  <LockIcon sx={{ color: "#fff" }} />
                ) : !isApplied && !readOnly ? (
                  <LocalOfferIcon />
                ) : undefined
              }
              disabled={readOnly ? true : isLocked ? false : loading}
              variant={isApplied && !readOnly ? "outlined" : "contained"}
              color={isApplied && !readOnly ? "error" : "primary"}
            >
              {readOnly 
                ? isLocked ? "Locked" : "Unlocked"
                : isApplied ? "Remove" : isLocked ? "Locked" : "Apply"
              }
            </Button>
          </Box>

          {/* Mobile Layout */}
          <Box
            sx={{
              display: { xs: "flex", sm: "none" },
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            {/* Discount Badge and Coupon Details */}
            <Box display="flex" gap={2}>
              {/* Discount Badge */}
              <Box
                bgcolor={
                  isApplied ? "#4CAF50" : isLocked ? "#9e9e9e" : "#F44336"
                }
                color="#fff"
                px={2}
                py={2}
                borderRadius="10px"
                fontWeight={700}
                fontSize="0.9rem"
                textAlign="center"
                minWidth="60px"
                height="fit-content"
                position="relative"
              >
                {discountDisplay}
                {isLocked && (
                  <LockIcon
                    sx={{
                      fontSize: 12,
                      position: "absolute",
                      top: 2,
                      right: 2,
                    }}
                  />
                )}
              </Box>

              {/* Coupon Details */}
              <Box flex={1} minWidth={0}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography fontWeight={600} fontSize="0.95rem" sx={{ wordBreak: "break-word" }}>
                    {coupon.name}
                  </Typography>
                  <Chip
                    label={coupon.discountConditionTypeName}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.65rem", height: 18 }}
                  />
                </Box>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  Code: <strong>{coupon.code}</strong>
                </Typography>
                {coupon.description && (
                  <Typography variant="body2" color="text.secondary" mt={0.5} sx={{ wordBreak: "break-word" }}>
                    {coupon.description}
                  </Typography>
                )}
                {isLocked && getUnlockMessage(coupon) && (
                  <Typography variant="body2" color="success.main" fontWeight={500} mt={0.5}>
                    {getUnlockMessage(coupon)}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Apply/Remove Button - Below content on mobile */}
            <Button
              onClick={() => {
                if (!readOnly) {
                  if (isApplied) {
                    handleRemoveCoupon();
                  } else {
                    handleApplyCoupon(coupon);
                  }
                }
              }}
              sx={{
                fontWeight: 600,
                ...(isLocked && {
                  backgroundColor: "#757575 !important",
                  color: "#fff !important",
                  opacity: "1 !important",
                }),
                ...(readOnly && isApplicable && {
                  backgroundColor: "#4CAF50 !important",
                  color: "#fff !important",
                  opacity: "1 !important",
                }),
              }}
              endIcon={
                isLocked ? (
                  <LockIcon sx={{ color: "#fff" }} />
                ) : !isApplied && !readOnly ? (
                  <LocalOfferIcon />
                ) : undefined
              }
              disabled={readOnly ? true : isLocked ? false : loading}
              variant={isApplied && !readOnly ? "outlined" : "contained"}
              color={isApplied && !readOnly ? "error" : "primary"}
              fullWidth
            >
              {readOnly 
                ? isLocked ? "Locked" : "Unlocked"
                : isApplied ? "Remove" : isLocked ? "Locked" : "Apply"
              }
            </Button>
          </Box>
        </Box>
      );
    };

    return (
      <>
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Coupons</Typography>

              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  color: "text.primary",
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{
              px: { xs: 1.5, md: 2 },
              display: "flex",
              flexDirection: "column",
              height: "100%",
              maxHeight: { xs: "calc(100vh - 200px)", md: "600px" },
            }}
          >
            <Box sx={{ flex: 1, overflowY: "auto", pr: { xs: 0.5, md: 1 } }}>
              {/* Manual Coupon Code Input - Only show if not read-only */}
              {!readOnly && (
                <Box display="flex" gap={1} mb={2}>
                  <TextField
                    size="small"
                    placeholder="Enter coupon code"
                    value={manualCode}
                    onChange={(e) =>
                      setManualCode(e.target.value.toUpperCase())
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleManualApply();
                    }}
                    fullWidth
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocalOfferIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleManualApply}
                    disabled={loading || !manualCode.trim()}
                    sx={{
                      fontWeight: 600,
                      flexShrink: 0,
                      borderRadius: "8px",
                      px: 3,
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Apply"
                    )}
                  </Button>
                </Box>
              )}

              {loadingCoupons ? (
                <Typography textAlign="center" py={4} color="text.secondary">
                  Loading coupons...
                </Typography>
              ) : coupons.length > 0 ? (
                <>
                  {/* Unlocked / Applicable Coupons */}
                  {unlockedCoupons.length > 0 && (
                    <Box mb={2}>
                      <Typography
                        variant="subtitle2"
                        color="success.main"
                        fontWeight={700}
                        mb={1}
                      >
                        Available Coupons ({unlockedCoupons.length})
                      </Typography>
                      {unlockedCoupons.map((coupon) =>
                        renderCouponCard(coupon, false),
                      )}
                    </Box>
                  )}

                  {/* Locked / Not-Applicable Coupons */}
                  {lockedCoupons.length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        fontWeight={700}
                        mb={1}
                      >
                        🔒 Add more items to unlock ({lockedCoupons.length})
                      </Typography>
                      {lockedCoupons.map((coupon) =>
                        renderCouponCard(coupon, true),
                      )}
                    </Box>
                  )}
                </>
              ) : (
                <Typography>No coupons available</Typography>
              )}
            </Box>

            {/* Footer message for read-only mode */}
            {readOnly && (
              <Box
                sx={{
                  mt: "auto",
                  pt: 2,
                  borderTop: "1px solid #e0e0e0",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 1.5,
                    color: "green",
                    fontStyle: "italic",
                    gap: 0.5,
                    textAlign: "center",
                  }}
                >
                  <TipsAndUpdatesIcon fontSize="small" />
                  Apply the unlocked coupons at checkout
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }
);

export default CouponPopup;